/**
 * 나이스(NEIS) 교육정보개방포털 API 유틸리티
 * https://open.neis.go.kr/
 */

const NEIS_API_BASE = 'https://open.neis.go.kr/hub';

// 학교 유형별 시간표 API 엔드포인트
const TIMETABLE_ENDPOINTS = {
  elementary: 'elsTimetable',
  middle: 'misTimetable',
  high: 'hisTimetable',
} as const;

export interface NeisTimetableParams {
  officeCode: string;   // ATPT_OFCDC_SC_CODE (시도교육청코드)
  schoolCode: string;   // SD_SCHUL_CODE (표준학교코드)
  year: number;         // 학년도 (예: 2024)
  semester: 1 | 2;      // 학기 (1 또는 2)
  grade: number;        // 학년 (1~6 초등, 1~3 중/고)
  classNum: number;     // 반 (1, 2, 3...)
  schoolType: 'elementary' | 'middle' | 'high';
  /** YYYYMMDD. 없으면 이번 주(월~금)를 조회하고, 비어 있으면 인접 주로 재시도 */
  from?: string;
  to?: string;
}

export interface TimetableEntry {
  period: number;
  time: string;
  monday?: string;
  tuesday?: string;
  wednesday?: string;
  thursday?: string;
  friday?: string;
}

export interface Timetable {
  semester: '1학기' | '2학기';
  year: number;
  entries: TimetableEntry[];
}

interface NeisTimetableRow {
  ALL_TI_YMD: string;
  PERIO: string;
  ITRT_CNTNT: string;
}

const DEFAULT_TIMES = ['09:00', '10:00', '11:00', '11:30', '13:20', '14:10', '15:00'];
const DAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'] as const;

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

function formatYmd(date: Date): string {
  return `${date.getFullYear()}${pad2(date.getMonth() + 1)}${pad2(date.getDate())}`;
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

/** 학년도·학기 (3~7월 1학기, 8~2월 2학기. 1~2월은 전년도 학년도) */
export function getAcademicYearAndSemester(date: Date = new Date()): { year: number; semester: 1 | 2 } {
  const month = date.getMonth() + 1;
  const calendarYear = date.getFullYear();
  if (month >= 3 && month <= 7) {
    return { year: calendarYear, semester: 1 };
  }
  if (month >= 8) {
    return { year: calendarYear, semester: 2 };
  }
  return { year: calendarYear - 1, semester: 2 };
}

/** 해당 날짜가 속한 주(월~금). 일요일은 다음 주로 본다. */
export function getWeekdayRange(date: Date = new Date()): { from: string; to: string } {
  const day = date.getDay(); // 0=일
  const mondayOffset = day === 0 ? 1 : 1 - day;
  const monday = addDays(date, mondayOffset);
  monday.setHours(0, 0, 0, 0);
  const friday = addDays(monday, 4);
  return { from: formatYmd(monday), to: formatYmd(friday) };
}

function getFirstWeekDates(year: number, semester: 1 | 2): { from: string; to: string } {
  if (semester === 1) {
    return { from: `${year}0302`, to: `${year}0306` };
  }
  return { from: `${year}0902`, to: `${year}0906` };
}

export function createEmptyTimetableEntries(): TimetableEntry[] {
  return Array.from({ length: 7 }, (_, i) => ({
    period: i + 1,
    time: DEFAULT_TIMES[i] || '09:00',
    monday: '',
    tuesday: '',
    wednesday: '',
    thursday: '',
    friday: '',
  }));
}

export function hasTimetableSubjects(entries: TimetableEntry[] | undefined): boolean {
  if (!entries || entries.length === 0) return false;
  return entries.some((entry) =>
    Boolean(
      entry.monday?.trim() ||
      entry.tuesday?.trim() ||
      entry.wednesday?.trim() ||
      entry.thursday?.trim() ||
      entry.friday?.trim()
    )
  );
}

function emptyTimetable(year: number, semester: 1 | 2): Timetable {
  return {
    semester: semester === 1 ? '1학기' : '2학기',
    year,
    entries: createEmptyTimetableEntries(),
  };
}

function parseNeisRows(data: unknown, endpoint: string): NeisTimetableRow[] {
  const timetableKey = endpoint;
  const record = data as Record<string, unknown>;
  let raw: unknown = record[timetableKey];
  if (raw == null && record.response && typeof record.response === 'object') {
    raw = (record.response as Record<string, unknown>)[timetableKey];
  }
  if (raw == null && typeof data === 'object' && data !== null) {
    const lowerKey = timetableKey.toLowerCase();
    const foundKey = Object.keys(record).find((k) => k.toLowerCase() === lowerKey);
    if (foundKey) raw = record[foundKey];
  }

  if (raw == null || raw === '') {
    return [];
  }

  let rows: NeisTimetableRow[] = [];
  let headArray: Array<{ RESULT?: { CODE?: string; MESSAGE?: string } }> | undefined;

  if (Array.isArray(raw)) {
    const rowBlock = raw.find((item: { row?: unknown }) => item.row != null);
    const headBlock = raw.find((item: { head?: unknown }) => Array.isArray(item.head));
    headArray = headBlock?.head;
    const r = rowBlock?.row;
    rows = Array.isArray(r) ? r : r && typeof r === 'object' && !Array.isArray(r) ? [r] : [];
  } else if (typeof raw === 'object' && raw !== null) {
    const obj = raw as { head?: unknown; row?: unknown };
    headArray = Array.isArray(obj.head) ? (obj.head as Array<{ RESULT?: { CODE?: string; MESSAGE?: string } }>) : undefined;
    const r = obj.row;
    rows = Array.isArray(r) ? r : r && typeof r === 'object' && !Array.isArray(r) ? [r] : [];
  } else {
    return [];
  }

  if (headArray?.length) {
    const resultBlock = headArray.find((h) => h.RESULT);
    const result = resultBlock?.RESULT;
    if (result?.CODE && result.CODE !== 'INFO-000' && result.CODE !== 'INFO-200') {
      throw new Error(result.MESSAGE || `나이스 API 오류: ${result.CODE}`);
    }
  }

  return rows;
}

function rowsToEntries(rows: NeisTimetableRow[]): TimetableEntry[] {
  const periodMap = new Map<number, Record<(typeof DAY_KEYS)[number], string>>();

  for (const row of rows) {
    const rawRow = row as unknown as Record<string, unknown>;
    const perio = rawRow.PERIO != null ? String(rawRow.PERIO) : '';
    const period = parseInt(perio, 10);
    if (isNaN(period) || period < 1 || period > 7) continue;

    const dateStr = rawRow.ALL_TI_YMD != null ? String(rawRow.ALL_TI_YMD) : '';
    if (!dateStr || dateStr.length !== 8) continue;

    const year = parseInt(dateStr.slice(0, 4), 10);
    const month = parseInt(dateStr.slice(4, 6), 10) - 1;
    const day = parseInt(dateStr.slice(6, 8), 10);
    const date = new Date(year, month, day);
    const dayOfWeek = date.getDay();
    if (dayOfWeek === 0 || dayOfWeek === 6) continue;

    const dayKey = DAY_KEYS[dayOfWeek - 1];
    if (!periodMap.has(period)) {
      periodMap.set(period, {
        monday: '',
        tuesday: '',
        wednesday: '',
        thursday: '',
        friday: '',
      });
    }

    const content = rawRow.ITRT_CNTNT != null ? String(rawRow.ITRT_CNTNT) : '';
    periodMap.get(period)![dayKey] = content.trim();
  }

  const maxPeriod = Math.max(...periodMap.keys(), 7);
  const entries: TimetableEntry[] = [];
  for (let p = 1; p <= maxPeriod; p++) {
    const dayMap = periodMap.get(p) ?? {
      monday: '',
      tuesday: '',
      wednesday: '',
      thursday: '',
      friday: '',
    };
    entries.push({
      period: p,
      time: DEFAULT_TIMES[p - 1] || '09:00',
      ...dayMap,
    });
  }
  return entries;
}

async function fetchNeisRowsForRange(
  params: NeisTimetableParams,
  from: string,
  to: string
): Promise<NeisTimetableRow[]> {
  const apiKey = process.env.REACT_APP_NEIS_API_KEY;
  if (!apiKey) {
    throw new Error('나이스 API 키가 설정되지 않았습니다. .env에 REACT_APP_NEIS_API_KEY를 추가해주세요.');
  }

  const endpoint = TIMETABLE_ENDPOINTS[params.schoolType];
  const searchParams = new URLSearchParams({
    KEY: apiKey,
    Type: 'json',
    pIndex: '1',
    pSize: '500',
    ATPT_OFCDC_SC_CODE: params.officeCode,
    SD_SCHUL_CODE: params.schoolCode,
    AY: String(params.year),
    SEM: String(params.semester),
    GRADE: String(params.grade),
    CLASS_NM: String(params.classNum),
    TI_FROM_YMD: from,
    TI_TO_YMD: to,
  });

  const url = `${NEIS_API_BASE}/${endpoint}?${searchParams.toString()}`;
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`나이스 API 요청 실패: ${response.status} ${response.statusText}`);
  }

  const data = await response.json();
  return parseNeisRows(data, endpoint);
}

function buildCandidateRanges(params: NeisTimetableParams): Array<{ from: string; to: string }> {
  if (params.from && params.to) {
    return [{ from: params.from, to: params.to }];
  }

  const current = getWeekdayRange();
  const currentMonday = new Date(
    Number(current.from.slice(0, 4)),
    Number(current.from.slice(4, 6)) - 1,
    Number(current.from.slice(6, 8))
  );
  const nextWeekMonday = addDays(currentMonday, 7);
  const prevWeekMonday = addDays(currentMonday, -7);

  return [
    current,
    { from: formatYmd(nextWeekMonday), to: formatYmd(addDays(nextWeekMonday, 4)) },
    { from: formatYmd(prevWeekMonday), to: formatYmd(addDays(prevWeekMonday, 4)) },
    getFirstWeekDates(params.year, params.semester),
  ];
}

/**
 * 나이스 API에서 시간표 조회.
 * 날짜를 지정하지 않으면 이번 주를 우선 조회하고, 비어 있으면 다음/이전 주·학기 첫 주로 재시도한다.
 */
export async function fetchNeisTimetable(params: NeisTimetableParams): Promise<Timetable> {
  const ranges = buildCandidateRanges(params);
  let lastEntries = createEmptyTimetableEntries();
  let lastError: unknown = null;
  let hadSuccess = false;

  for (const range of ranges) {
    try {
      const rows = await fetchNeisRowsForRange(params, range.from, range.to);
      const entries = rowsToEntries(rows);
      lastEntries = entries;
      hadSuccess = true;
      if (hasTimetableSubjects(entries)) {
        return {
          semester: params.semester === 1 ? '1학기' : '2학기',
          year: params.year,
          entries,
        };
      }
    } catch (error) {
      lastError = error;
      console.warn('나이스 시간표 구간 조회 실패:', range, error);
    }
  }

  if (!hadSuccess && lastError instanceof Error) {
    throw lastError;
  }

  return {
    semester: params.semester === 1 ? '1학기' : '2학기',
    year: params.year,
    entries: lastEntries,
  };
}

export function createEmptyTimetable(year?: number, semester?: 1 | 2): Timetable {
  const academic = getAcademicYearAndSemester();
  return emptyTimetable(year ?? academic.year, semester ?? academic.semester);
}

export type NeisMealType = '조식' | '중식' | '석식';

export interface NeisMealParams {
  officeCode: string;
  schoolCode: string;
  /** YYYYMMDD. 없으면 오늘 */
  date?: string;
}

export interface NeisMealItem {
  type: NeisMealType;
  dishes: string[];
  calorie?: string;
}

export interface NeisMealDay {
  date: string;
  meals: NeisMealItem[];
}

interface NeisMealRow {
  MLSV_YMD?: string;
  MMEAL_SC_CODE?: string;
  MMEAL_SC_NM?: string;
  DDISH_NM?: string;
  CAL_INFO?: string;
}

const MEAL_TYPE_ORDER: NeisMealType[] = ['중식', '조식', '석식'];

function parseMealDishes(raw: string): string[] {
  return raw
    .split(/<br\s*\/?>/i)
    .map((item) => item.replace(/&amp;/g, '&').replace(/\s+/g, ' ').trim())
    .filter(Boolean);
}

function normalizeMealType(row: NeisMealRow): NeisMealType | null {
  const name = String(row.MMEAL_SC_NM || '').trim();
  if (name === '조식' || name === '중식' || name === '석식') return name;
  const code = String(row.MMEAL_SC_CODE || '').trim();
  if (code === '1') return '조식';
  if (code === '2') return '중식';
  if (code === '3') return '석식';
  return null;
}

/**
 * 나이스 급식식단정보 API에서 특정 일자(기본: 오늘) 급식을 조회한다.
 */
export async function fetchNeisMealToday(params: NeisMealParams): Promise<NeisMealDay> {
  const apiKey = process.env.REACT_APP_NEIS_API_KEY;
  if (!apiKey) {
    throw new Error('나이스 API 키가 설정되지 않았습니다. .env에 REACT_APP_NEIS_API_KEY를 추가해주세요.');
  }

  const officeCode = params.officeCode.trim();
  const schoolCode = params.schoolCode.trim();
  if (!officeCode || !schoolCode) {
    throw new Error('학교 나이스 코드가 설정되지 않았습니다.');
  }

  const date = params.date || formatYmd(new Date());
  const searchParams = new URLSearchParams({
    KEY: apiKey,
    Type: 'json',
    pIndex: '1',
    pSize: '100',
    ATPT_OFCDC_SC_CODE: officeCode,
    SD_SCHUL_CODE: schoolCode,
    MLSV_YMD: date,
  });

  const url = `${NEIS_API_BASE}/mealServiceDietInfo?${searchParams.toString()}`;
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`나이스 급식 API 요청 실패: ${response.status} ${response.statusText}`);
  }

  const data = await response.json();
  const rows = parseNeisRows(data, 'mealServiceDietInfo') as unknown as NeisMealRow[];

  const meals: NeisMealItem[] = [];
  for (const row of rows) {
    const type = normalizeMealType(row);
    if (!type) continue;
    const dishes = parseMealDishes(String(row.DDISH_NM || ''));
    if (dishes.length === 0) continue;
    const calorie = String(row.CAL_INFO || '').trim() || undefined;
    meals.push({ type, dishes, calorie });
  }

  meals.sort(
    (a, b) => MEAL_TYPE_ORDER.indexOf(a.type) - MEAL_TYPE_ORDER.indexOf(b.type)
  );

  return { date, meals };
}
